import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVAL_LABEL = { monthly: "month", six_monthly: "6 months", annual: "year" };

function fmt(p) { return `£${((p || 0) / 100).toFixed(2)}`; }

export default function PublicMembership() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/venues/${slug}/memberships`);
        setData(data);
      } catch (e) {
        setErr("Venue not found");
      }
    })();
  }, [slug]);

  if (err) return <div className="min-h-screen grid place-items-center text-zinc-500">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-zinc-500 text-sm">Loading…</div>;

  const { venue, tiers } = data;
  const brand = venue.brand_color || "#4338CA";

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="public-membership-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-3">
          {venue.logo_url ? (
            <img src={venue.logo_url} alt={venue.name} className="h-10 w-10 object-contain rounded-md" />
          ) : (
            <div className="h-10 w-10 grid place-items-center rounded-md text-white font-display font-black" style={{ background: brand }}>{venue.name?.[0]}</div>
          )}
          <div className="font-display font-black text-lg">{venue.name}</div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium">Membership</div>
        <h1 className="mt-2 font-display font-black text-4xl md:text-5xl tracking-tighter">Become a supporter</h1>
        <p className="mt-3 text-zinc-600 max-w-xl">Pick a tier below and join the community. Your recurring support directly funds new shows and keeps the doors open. Cancel any time.</p>

        {tiers.length === 0 && (
          <div className="mt-10 rounded-xl border border-dashed border-zinc-300 p-12 text-center text-zinc-500">
            No membership tiers available yet.
          </div>
        )}

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiers.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelectedTier(t)}
              data-testid={`public-tier-${t.id}`}
              className="text-left rounded-2xl border-2 border-zinc-200 bg-white p-6 hover:border-zinc-900 transition"
            >
              <div className="font-display font-black text-xl">{t.label}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-mono font-black text-3xl">{fmt(t.price_pence)}</span>
                <span className="text-sm text-zinc-500">/ {INTERVAL_LABEL[t.interval]}</span>
              </div>
              {t.description && <p className="mt-3 text-sm text-zinc-600">{t.description}</p>}
              {t.perks?.length > 0 && (
                <ul className="mt-4 space-y-1.5 text-sm">
                  {t.perks.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: brand }} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-5 text-sm font-semibold" style={{ color: brand }}>Join →</div>
            </button>
          ))}
        </div>
      </main>

      {selectedTier && (
        <SignupDialog
          venue={venue}
          slug={slug}
          tier={selectedTier}
          onClose={() => setSelectedTier(null)}
        />
      )}
    </div>
  );
}

function SignupDialog({ venue, slug, tier, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [autoRenew, setAutoRenew] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const brand = venue.brand_color || "#4338CA";
  const requiredQs = tier.custom_questions || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    for (const q of requiredQs) {
      if (q.required && !answers[q.id]?.trim?.()) {
        toast.error(`Please answer: ${q.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/venues/${slug}/memberships/checkout`, {
        tier_id: tier.id,
        member_email: email.trim(),
        member_name: name.trim(),
        auto_renew: autoRenew,
        answers,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-up failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="signup-dialog">
      <div className="max-w-md w-full bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Joining</div>
            <div className="font-display font-black text-2xl mt-0.5">{tier.label}</div>
            <div className="mt-1 text-sm text-zinc-600">
              {fmt(tier.price_pence)} / {INTERVAL_LABEL[tier.interval]} · <span className="capitalize">{venue.name}</span>
            </div>
          </div>
          <Sparkles className="w-5 h-5" style={{ color: brand }} />
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <Label>Your name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" required data-testid="signup-name" className="mt-1" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="signup-email" className="mt-1" />
          </div>

          {requiredQs.map((q) => (
            <div key={q.id}>
              <Label>{q.label}{q.required ? " *" : ""}</Label>
              <Input
                value={answers[q.id] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                data-testid={`signup-answer-${q.id}`}
                className="mt-1"
              />
            </div>
          ))}

          <label className="flex items-start gap-3 rounded-xl border border-zinc-200 p-3 cursor-pointer hover:border-zinc-300">
            <input
              type="checkbox"
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
              className="mt-1"
              data-testid="signup-auto-renew"
            />
            <div className="text-sm">
              <div className="font-medium">Renew automatically</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {autoRenew
                  ? `You'll be charged ${fmt(tier.price_pence)} every ${INTERVAL_LABEL[tier.interval]}. Cancel any time.`
                  : "One-off payment — your membership will end at the next renewal date."}
              </div>
            </div>
          </label>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="signup-submit"
            className="w-full h-12 rounded-full text-white text-base font-semibold"
            style={{ background: brand }}
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redirecting to Stripe…</> : `Join for ${fmt(tier.price_pence)}`}
          </Button>
          <button type="button" onClick={onClose} className="w-full text-sm text-zinc-500 hover:text-zinc-900" data-testid="signup-cancel">
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
}
